import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { RegisterFormData } from '@/types';

interface ApiErrorResponse {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
}

const registerSchema = z.object({
  codigoTenant: z.string()
    .min(1, 'Código é obrigatório')
    .length(3, 'Código deve ter 3 caracteres')
    .regex(/^[0-9]+$/, 'Código deve conter apenas números'),
  nome: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido').min(1, 'Email é obrigatório'),
  telefone: z.string()
    .min(1, 'Telefone é obrigatório')
    .refine((value) => /^\D*(\d\D*){10,11}$/.test(value), 'Telefone deve ter 10 ou 11 dígitos'),
  cargo: z.string().trim().min(1, 'Cargo é obrigatório').max(100, 'Cargo deve ter no máximo 100 caracteres'),
  senha: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  confirmarSenha: z.string().min(1, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.senha === data.confirmarSenha, {
  message: 'Senhas não coincidem',
  path: ['confirmarSenha'],
});

type RegisterFormDataWithConfirm = RegisterFormData & {
  confirmarSenha: string;
};

export const RegisterPage: React.FC = () => {
  const { register: registerUser, login, isLoading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerNotice, setRegisterNotice] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormDataWithConfirm>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormDataWithConfirm) => {
    try {
      setIsSubmitting(true);
      setRegisterNotice(null);
      const registerData: RegisterFormData = {
        codigoTenant: data.codigoTenant,
        nome: data.nome,
        email: data.email,
        telefone: data.telefone.trim(),
        cargo: data.cargo.trim(),
        senha: data.senha,
      };
      await registerUser(registerData);
      await login({
        email: data.email,
        senha: data.senha,
        codigoTenant: data.codigoTenant,
      });
    } catch (error: unknown) {
      const typedError = error as ApiErrorResponse;
      const backendMessage =
        typedError.response?.data?.error ||
        typedError.response?.data?.message;
      if (backendMessage) {
        setRegisterNotice(backendMessage);
      } else {
        setRegisterNotice('Não foi possível concluir seu cadastro agora. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Sistema de Estágio
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            CODITECH - Crie sua conta
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Criar Conta</CardTitle>
            <CardDescription>
              Cadastre sua conta e comece no plano gratuito
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="codigoTenant">Codigo de identificação da unidade</Label>
                <Input
                  id="codigoTenant"
                  type="text"
                  placeholder="Digite o código ex: 000 ou 999"
                  maxLength={3}
                  {...register('codigoTenant')}
                  className={errors.codigoTenant ? 'border-error-500' : ''}
                />
                {errors.codigoTenant && (
                  <p className="mt-1 text-sm text-error-600">{errors.codigoTenant.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="nome">Nome Completo</Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Seu nome completo"
                  {...register('nome')}
                  className={errors.nome ? 'border-error-500' : ''}
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-error-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  {...register('email')}
                  className={errors.email ? 'border-error-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  maxLength={20}
                  {...register('telefone')}
                  className={errors.telefone ? 'border-error-500' : ''}
                />
                {errors.telefone && (
                  <p className="mt-1 text-sm text-error-600">{errors.telefone.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="cargo">Cargo</Label>
                <Input
                  id="cargo"
                  type="text"
                  placeholder="Ex.: Diretor, coordenador, secretário"
                  maxLength={100}
                  {...register('cargo')}
                  className={errors.cargo ? 'border-error-500' : ''}
                />
                {errors.cargo && (
                  <p className="mt-1 text-sm text-error-600">{errors.cargo.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  {...register('senha')}
                  className={errors.senha ? 'border-error-500' : ''}
                />
                {errors.senha && (
                  <p className="mt-1 text-sm text-error-600">{errors.senha.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="confirmarSenha">Confirmar Senha</Label>
                <Input
                  id="confirmarSenha"
                  type="password"
                  placeholder="Confirme sua senha"
                  {...register('confirmarSenha')}
                  className={errors.confirmarSenha ? 'border-error-500' : ''}
                />
                {errors.confirmarSenha && (
                  <p className="mt-1 text-sm text-error-600">{errors.confirmarSenha.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? 'Criando conta...' : 'Criar Conta'}
              </Button>
            </form>

            {registerNotice && (
              <div className="mt-4 rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800" role="alert">
                {registerNotice}
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Já tem uma conta?{' '}
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  Faça login aqui
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
